import React, { useState } from 'react';
import { contactInfo } from '../mock';
import { MapPin, Phone, Clock, Instagram, Send } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.status === 200) {
        toast({
          title: "Message Sent!",
          description: response.data.message || "Thank you for contacting us. We'll get back to you soon.",
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again or call us directly.",
        variant: "destructive"
      });
    }
  };

  return (
    <section id="contact" className="py-24 bg-[#f7f5f2]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-5xl md:text-6xl font-bold text-[#1a1a2e] tracking-tight">
            Visit Our Showroom
          </h2>
          <p className="text-lg text-[#666] max-w-2xl mx-auto">
            Experience our collection in person or get in touch with us
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="space-y-8">
            <div className="bg-white p-8 rounded-sm shadow-lg space-y-6">
              <h3 className="text-2xl font-bold text-[#1a1a2e]">Contact Information</h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#dc143c] rounded-sm flex items-center justify-center">
                    <MapPin size={20} className="text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#666] uppercase tracking-wider mb-1">Address</p>
                    <p className="text-base text-[#1a1a2e] font-medium">{contactInfo.address}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#dc143c] rounded-sm flex items-center justify-center">
                    <Phone size={20} className="text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#666] uppercase tracking-wider mb-1">Phone</p>
                    <a
                      href={`tel:${contactInfo.phone}`}
                      className="text-base text-[#1a1a2e] font-medium hover:text-[#dc143c] transition-colors"
                    >
                      {contactInfo.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#dc143c] rounded-sm flex items-center justify-center">
                    <Clock size={20} className="text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#666] uppercase tracking-wider mb-1">Business Hours</p>
                    <p className="text-base text-[#1a1a2e] font-medium">
                      Weekdays: {contactInfo.hours.weekdays}
                    </p>
                    <p className="text-base text-[#1a1a2e] font-medium">
                      Weekends: {contactInfo.hours.weekends}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-purple-600 to-pink-600 rounded-sm flex items-center justify-center">
                    <Instagram size={20} className="text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#666] uppercase tracking-wider mb-1">Instagram</p>
                    <a
                      href={contactInfo.social.instagram}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-base text-[#1a1a2e] font-medium hover:text-[#dc143c] transition-colors"
                    >
                      @bhargavashoesbti
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-2 rounded-sm shadow-lg">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3438.3!2d74.9519!3d30.2108!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x391718d57730ca41%3A0x9a98f98f36a6b60!2sSadar%20Bazaar%2C%20Bathinda%2C%20Punjab%20151001!5e0!3m2!1sen!2sin!4v1234567890"
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Bhargava Shoes Location"
                className="rounded-sm"
              ></iframe>
            </div>
          </div>

          <div className="bg-white p-8 rounded-sm shadow-lg">
            <h3 className="text-2xl font-bold text-[#1a1a2e] mb-6">Send Us a Message</h3>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-[#666] mb-2 uppercase tracking-wider">
                  Your Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-[#f7f5f2] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#dc143c] transition-all duration-300"
                  placeholder="Enter your name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-[#666] mb-2 uppercase tracking-wider">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-[#f7f5f2] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#dc143c] transition-all duration-300"
                  placeholder="Enter your email"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-[#666] mb-2 uppercase tracking-wider">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-[#f7f5f2] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#dc143c] transition-all duration-300"
                  placeholder="Enter your phone number"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-[#666] mb-2 uppercase tracking-wider">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="w-full px-4 py-3 border border-[#f7f5f2] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#dc143c] transition-all duration-300 resize-none"
                  placeholder="How can we help you?"
                ></textarea>
              </div>

              <button
                type="submit"
                className="w-full flex items-center justify-center gap-3 bg-[#dc143c] text-white px-8 py-4 rounded-sm text-base font-medium hover:bg-[#b01030] transition-all duration-300"
              >
                <Send size={18} />
                <span>Send Message</span>
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
